import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type { OAISchema } from '~/types/openapi';
import { MARKET_API_URL } from '~/utils/url';

// ============================================================================
// Response 타입 (OAISchema 기반)
// ============================================================================

/** 채팅 메시지 첨부파일 응답 */
export type ChatAttachmentResponse = OAISchema['ChatAttachmentResponse'];

/** 채팅 메시지 응답 */
export type ChatMessageResponse = OAISchema['ChatMessageResponse'];

/** 채팅방 응답 */
export type ChatRoomResponse = OAISchema['ChatRoomResponse'];

/** 알람 채팅방 응답 */
export type AlarmChatRoomResponse = OAISchema['AlarmChatRoomResponse'];

/** 파일 업로드 URL 응답 */
export type UploadUrlResponse = OAISchema['UploadUrlResponse'];

// ============================================================================
// Request Body 타입 (OAISchema 기반)
// ============================================================================

/** 메시지 전송 요청 */
export type SendMessageBody = OAISchema['SendMessageRequest'];

/** 파일 업로드 URL 요청 */
export type UploadFileBody = OAISchema['UploadUrlRequest'];

/** 채팅방 생성 요청 */
export type CreateRoomBody = OAISchema['CreateRoomRequest'];

// ============================================================================
// Query Params 타입
// ============================================================================

export type GetMessagesParams = {
  offset: number;
  limit: number;
};

// ============================================================================
// DataSource Interface & Implementation
// ============================================================================

export interface ChattingDataSource {
  /** 전체 읽지 않은 메시지 수 조회 */
  getTotalUnreadCount(): Promise<Record<string, number>>;

  /** 채팅 메시지 목록 조회 */
  getMessages(
    roomId: string,
    params: GetMessagesParams,
  ): Promise<ChatMessageResponse[]>;

  /** 메시지 전송 */
  sendMessage(body: SendMessageBody): Promise<ChatMessageResponse>;

  /** 타이핑 이벤트 전송 */
  sendTyping(roomId: string, clientId: string): Promise<void>;

  /** 파일 업로드 (presigned URL 획득) */
  uploadFile(body: UploadFileBody): Promise<UploadUrlResponse[]>;

  /** 파일 다운로드 URL 획득 */
  getFileDownloadUrl(
    fileId: string,
    params: { roomId: string; s3Key: string },
  ): Promise<string>;

  /** 알람 채팅방 목록 조회 */
  getAlarmChatRooms(): Promise<AlarmChatRoomResponse[]>;

  /** 단일 알람 채팅방 조회 */
  getAlarmChatRoom(roomId: number): Promise<AlarmChatRoomResponse>;

  /** 채팅방 푸시 알림 수신 여부 조회 */
  getPushEnabled(roomId: string): Promise<Record<string, boolean>>;

  /** 채팅방 푸시 알림 수신 여부 설정 */
  setPushEnabled(roomId: string, enabled: boolean): Promise<void>;

  /** 채팅방 생성 */
  createRoom(body: CreateRoomBody): Promise<ChatRoomResponse>;
}

export class ChattingDataSourceImpl implements ChattingDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getTotalUnreadCount() {
    return this.client.request<Record<string, number>>({
      url: '/api/market-place/chat/unread-count',
      method: 'get',
    });
  }

  async getMessages(roomId: string, params: GetMessagesParams) {
    return this.client.request<ChatMessageResponse[]>({
      url: `/api/market-place/chat/rooms/${roomId}/messages`,
      method: 'get',
      params,
    });
  }

  async sendMessage(body: SendMessageBody) {
    return this.client.request<ChatMessageResponse>({
      url: '/api/market-place/chat/messages',
      method: 'post',
      data: body,
    });
  }

  async sendTyping(roomId: string, clientId: string) {
    await this.client.request<void>({
      url: `/api/market-place/chat/rooms/${roomId}/typing`,
      method: 'post',
      params: { clientId },
    });
  }

  async uploadFile(body: UploadFileBody) {
    return this.client.request<UploadUrlResponse[]>({
      url: '/api/market-place/chat/files/upload',
      method: 'post',
      data: body,
    });
  }

  async getFileDownloadUrl(
    fileId: string,
    params: { roomId: string; s3Key: string },
  ) {
    return this.client.request<string>({
      url: `/api/market-place/chat/files/${fileId}`,
      method: 'get',
      params,
    });
  }

  async getAlarmChatRooms() {
    return this.client.request<AlarmChatRoomResponse[]>({
      url: '/api/market-place/chat/rooms/alarm',
      method: 'get',
    });
  }

  async getAlarmChatRoom(roomId: number) {
    return this.client.request<AlarmChatRoomResponse>({
      url: `/api/market-place/chat/rooms/${roomId}`,
      method: 'get',
    });
  }

  async getPushEnabled(roomId: string) {
    return this.client.request<Record<string, boolean>>({
      url: `/api/market-place/chat/rooms/${roomId}/push-enabled`,
      method: 'get',
    });
  }

  async setPushEnabled(roomId: string, enabled: boolean) {
    await this.client.request<void>({
      url: `/api/market-place/chat/rooms/${roomId}/push-enabled`,
      method: 'post',
      params: { enabled },
    });
  }

  async createRoom(body: CreateRoomBody) {
    return this.client.request<ChatRoomResponse>({
      url: '/api/market-place/chat/rooms',
      method: 'post',
      data: body,
    });
  }
}

export const chattingDataSource = new ChattingDataSourceImpl();

// ============================================================================
// SSE 스트림 URL 생성 (EventSource는 DataSource 외부에서 직접 생성)
// ============================================================================

/**
 * SSE 스트림 URL 반환
 * EventSource는 직접 fetch가 아닌 브라우저 API이므로 URL만 반환
 */
export function getChattingStreamUrl(roomId: string, clientId: string): string {
  return `${MARKET_API_URL}/api/market-place/chat/rooms/${roomId}/stream?clientId=${encodeURIComponent(
    clientId,
  )}`;
}
